import {
	type ValidateOrderQuoteOfferBatchForClientQuery,
	usePreviewQuoteRecipientsQuery,
	useValidateOrderQuoteOfferBatchForClientQuery,
} from '@codezo/graphql';
import { AlertTriangle, Check, Loader2, Mail, Send } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { formatDate } from '../../lib/utils';
import { SHIPPING_METHOD_LABEL } from '../../lib/status-configs';
import { GUEST_EMAIL_RE } from './modals/SendGuestLinkModal';
import { QuoteRecipientsLine } from './QuoteRecipientsLine';
import { Button } from '../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/* ════════════════════════ SEND-TO-CLIENT PRE-FLIGHT ════════════════════════ */

interface SendToClientDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The quote the batch belongs to — for the "Will be emailed to" preview. */
	quoteId: string;
	/** The quote has chosen client contacts, so a fall-back to everyone is worth flagging. */
	hasClientContacts?: boolean;
	/**
	 * The batch being sent. `null` when the dialog is closed.
	 *
	 * Plans are gone; their warnings folded into the tier list, where a split
	 * reports its own allocation gap against the tier it sits on.
	 */
	target: { id: string; name: string } | null;
	/** Loading state of the send mutation itself. */
	loading?: boolean;
	/**
	 * `guestEmail` is the optional address to also send a password-less
	 * acceptance link to, once the batch itself has gone out.
	 */
	onConfirm: (guestEmail?: string) => void;
}

/**
 * Pre-flight check for sending an offer batch to the client. Every reason shown
 * is the backend's own message (`validateOrderQuoteOfferBatchForClient`):
 *   - `canSend` gates the Send button (the pricing+delivery floor).
 *   - `errors`  are hard blockers; `warnings` are advisory and never block.
 *   - `valid`   is the all-clear (no warnings).
 */
export function SendToClientDialog({
	open,
	onOpenChange,
	quoteId,
	hasClientContacts = false,
	target,
	loading,
	onConfirm,
}: SendToClientDialogProps) {
	const {
		data,
		loading: validating,
		error,
	} = useValidateOrderQuoteOfferBatchForClientQuery({
		variables: { id: target?.id ?? '' },
		skip: !open || !target?.id,
		fetchPolicy: 'no-cache',
	});

	// Who the offer email goes to — the backend's own rule (chosen contacts, else
	// everyone at the client, plus guest-link holders).
	const {
		data: recipientsData,
		loading: loadingRecipients,
		error: recipientsError,
	} = usePreviewQuoteRecipientsQuery({
		variables: { orderQuoteId: quoteId },
		skip: !open,
		fetchPolicy: 'no-cache',
	});

	const [guestEmail, setGuestEmail] = useState('');

	useEffect(() => {
		if (open) setGuestEmail('');
	}, [open]);

	const result = data?.validateOrderQuoteOfferBatchForClient;
	const errors = result?.errors ?? [];
	const warnings = result?.warnings ?? [];
	/* One card per product rather than a flat list.
	 *
	 * Every warning names the product it is about, so a flat list printed that
	 * name on every row — three notes on two products repeated "Keychain" twice
	 * and buried the thing that actually differed, which is the tier. Grouped, the
	 * name is said once and the rows underneath are free to be about tiers.
	 *
	 * First-appearance order, not sorted: the backend returns them per offer in
	 * the batch's own order, and re-sorting here would put the products in an
	 * order nothing else on the page uses.
	 */
	const warningsByProduct = groupByProduct(warnings);

	const valid = result?.valid === true;
	// Sending is gated on `canSend` (the pricing+delivery floor) only — warnings never block.
	const canSend = result?.canSend === true;
	// A malformed guest address blocks the send too — the link is created straight
	// after, and a typo there would silently cost the guest their access.
	const trimmedGuestEmail = guestEmail.trim();
	const guestEmailValid = !trimmedGuestEmail || GUEST_EMAIL_RE.test(trimmedGuestEmail);
	const sendEnabled = canSend && guestEmailValid && !validating && !error;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Send to client</DialogTitle>
					<DialogDescription>
						{validating
							? `Checking whether "${target?.name}" is ready to send…`
							: canSend
								? `Review the details below, then send "${target?.name}" to the client.`
								: `"${target?.name}" isn't ready to send yet.`}
					</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-4 pt-2">
					{validating ? (
						<div className="flex items-center gap-2 px-1 py-3 text-sm text-text-3">
							<Loader2 size={14} className="animate-spin" />
							Checking requirements…
						</div>
					) : error ? (
						<NoticeRow
							tone="danger"
							title="Unable to check requirements"
							detail="Close and try again in a moment."
						/>
					) : (
						<>
							{errors.length > 0 && (
								<Section label="Before sending">
									{errors.map((err) => (
										<NoticeRow
											key={err.key}
											tone="danger"
											title={err.message}
										/>
									))}
								</Section>
							)}

							{warnings.length > 0 && (
								<Section
									label="Notes"
									right={
										warningsByProduct.length > 1
											? `${warnings.length} on ${warningsByProduct.length} products`
											: undefined
									}
								>
									{warningsByProduct.map((group) => (
										<ProductNotes
											key={group.offerId}
											name={group.offerName}
											count={group.count}
											offerLevel={group.offerLevel}
											tiers={group.tiers}
										/>
									))}
								</Section>
							)}

							{valid && <NoticeRow tone="success" title="All requirements met." />}

							{canSend && (
								<div className="flex items-start gap-2 border-t border-line-1 pt-4 text-sm text-text-4">
									<Mail size={14} className="mt-0.5 shrink-0" />
									<QuoteRecipientsLine
										loading={loadingRecipients}
										failed={!!recipientsError}
										emails={recipientsData?.previewQuoteRecipients?.emails}
										nominated={
											recipientsData?.previewQuoteRecipients?.nominated
										}
										listIgnored={
											hasClientContacts &&
											recipientsData?.previewQuoteRecipients?.nominated ===
												false
										}
									/>
								</div>
							)}

							{/* Optional guest link, created right after the batch goes out. */}
							{canSend && (
								<div className="flex flex-col gap-1.5 border-t border-line-1 pt-4">
									<Label>Guest acceptance link</Label>
									<Input
										type="email"
										value={guestEmail}
										onChange={(e) => setGuestEmail(e.target.value)}
										placeholder="name@company.com"
										disabled={loading}
									/>
									<div
										className={`text-sm ${guestEmailValid ? 'text-text-4' : 'text-status-danger'}`}
									>
										{guestEmailValid
											? 'Optional — also email a link to someone without an account. It opens the whole quote, and only that address can use it.'
											: 'Enter a valid email address, or leave it empty.'}
									</div>
								</div>
							)}
						</>
					)}
				</DialogBody>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						variant="default"
						onClick={() => onConfirm(trimmedGuestEmail || undefined)}
						disabled={loading || !sendEnabled}
					>
						<Send size={14} data-icon="inline-start" />
						{loading ? 'Sending…' : 'Send to client'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function Section({
	label,
	right,
	children,
}: {
	label: string;
	/** A count, when there is more than one thing being counted. */
	right?: string;
	children: ReactNode;
}) {
	return (
		<div>
			<div className="mb-2 flex items-baseline justify-between gap-2">
				<span className="text-2xs font-medium tracking-[0.05em] text-text-4 uppercase">
					{label}
				</span>
				{right && <span className="text-2xs text-text-4">{right}</span>}
			</div>
			<div className="flex flex-col gap-1.5">{children}</div>
		</div>
	);
}

/** One validation warning, as the batch check returns it. */
type Warning = NonNullable<
	ValidateOrderQuoteOfferBatchForClientQuery['validateOrderQuoteOfferBatchForClient']
>['warnings'][number];

/**
 * Warnings by product, then by quantity tier, in first-appearance order.
 *
 * Two levels because the payload repeats itself twice over: every warning names
 * its product, and most also name their tier. A flat list of three notes on one
 * product printed "tester 2" three times and "20 pcs" three times, leaving the
 * one word that differed — Standard, Express — at the end of the third line.
 *
 * Not memoised: `warnings` is a fresh array on every render (it falls back to
 * `[]`), so a `useMemo` keyed on it would recompute every time anyway while
 * looking like it didn't. There are a handful of warnings at most.
 */
function groupByProduct(warnings: Warning[]) {
	const groups = new Map<
		string,
		{
			offerId: string;
			offerName: string;
			count: number;
			/** Warnings belonging to no single tier — they are about the offer. */
			offerLevel: Warning[];
			tiers: Map<string, { label: string; warnings: Warning[] }>;
		}
	>();

	for (const warn of warnings) {
		let group = groups.get(warn.offerId);
		if (!group) {
			group = {
				offerId: warn.offerId,
				offerName: warn.offerName,
				count: 0,
				offerLevel: [],
				tiers: new Map(),
			};
			groups.set(warn.offerId, group);
		}
		group.count += 1;

		const tierId = warn.orderQuoteItemQuantityId;
		if (!tierId) {
			group.offerLevel.push(warn);
			continue;
		}
		const tier = group.tiers.get(tierId);
		if (tier) tier.warnings.push(warn);
		else
			group.tiers.set(tierId, {
				label: warn.quantity != null ? `${warn.quantity.toLocaleString()} pcs` : 'One tier',
				warnings: [warn],
			});
	}

	return [...groups.values()].map((group) => ({
		...group,
		tiers: [...group.tiers.values()],
	}));
}

/**
 * Every note about one product, under its name.
 *
 * The name is the card's heading and each tier is a sub-heading, so the rows
 * inside say only what differs — the method, when it would land, how late. That
 * is the point of grouping: a tier with three problems reads as one tier with
 * three problems, rather than three unrelated notices that happen to start with
 * the same two words.
 */
function ProductNotes({
	name,
	count,
	offerLevel,
	tiers,
}: {
	name: string;
	count: number;
	offerLevel: Warning[];
	tiers: Array<{ label: string; warnings: Warning[] }>;
}) {
	return (
		<div className="overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken">
			<div className="flex items-baseline justify-between gap-2 px-3 pt-2.5 pb-1.5">
				<span className="min-w-0 truncate text-sm font-medium text-text-1">{name}</span>
				{count > 1 && <span className="shrink-0 text-2xs text-text-4">{count} notes</span>}
			</div>

			{/* Whole-offer notes first, and without a tier heading — they are true of
			    the product however much of it is bought. */}
			{offerLevel.length > 0 && (
				<div className="border-t border-line-1">
					{offerLevel.map((warn) => (
						<WarningRow key={rowKey(warn)} warn={warn} />
					))}
				</div>
			)}

			{tiers.map((tier) => (
				<div key={tier.label} className="border-t border-line-1">
					<div className="px-3 pt-2 pb-0.5 font-mono text-2xs text-text-3">
						{tier.label}
					</div>
					{tier.warnings.map((warn) => (
						<WarningRow key={rowKey(warn)} warn={warn} />
					))}
				</div>
			))}
		</div>
	);
}

/** Distinct per row: the same key can appear twice on one tier, once per method. */
const rowKey = (warn: Warning) =>
	`${warn.key}:${warn.orderQuoteItemQuantityId ?? ''}:${warn.deliveryId ?? ''}:${warn.shippingMethod ?? ''}`;

/**
 * One note, and what is left to say about it.
 *
 * Neither the product nor the tier — both are headings above it. What remains is
 * the option it is about, and the deadline on the one warning that still carries
 * one.
 *
 * No arrival date and no overshoot. Send validation used to report feasibility
 * here as well, which meant one batch-level warning had to pick a single tier
 * and option to speak for every one of them. That verdict now lives only on each
 * delivery option's own badge, where it is about the option being looked at.
 */
function WarningRow({ warn }: { warn: Warning }) {
	const context = [
		warn.shippingMethod ? SHIPPING_METHOD_LABEL[warn.shippingMethod] : null,
		warn.deadline ? `needed by ${formatDate(warn.deadline)}` : null,
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<div className="flex items-start gap-2.5 px-3 py-1.5">
			<span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-warn/10 text-status-warn">
				<AlertTriangle size={11} strokeWidth={2.75} />
			</span>
			<div className="min-w-0 pb-1">
				<div className="text-sm text-text-1">{warn.message}</div>
				{context && <div className="mt-0.5 text-sm text-text-4">{context}</div>}
			</div>
		</div>
	);
}

const TONES = {
	danger: { Icon: AlertTriangle, badge: 'bg-status-danger/10 text-status-danger' },
	warn: { Icon: AlertTriangle, badge: 'bg-status-warn/10 text-status-warn' },
	success: { Icon: Check, badge: 'bg-status-success/10 text-status-success' },
} as const;

function NoticeRow({
	tone,
	title,
	detail,
}: {
	tone: keyof typeof TONES;
	title: string;
	detail?: string;
}) {
	const { Icon, badge } = TONES[tone];
	return (
		<div className="flex items-start gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5">
			<span
				className={`mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${badge}`}
			>
				<Icon size={11} strokeWidth={2.75} />
			</span>
			<div className="min-w-0">
				<div
					className={`text-sm font-medium ${tone === 'success' ? 'text-text-2' : 'text-text-1'}`}
				>
					{title}
				</div>
				{detail && <div className="mt-0.5 text-sm text-text-4">{detail}</div>}
			</div>
		</div>
	);
}
